import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { cn } from "@/lib/utils";

const navMarkBase = cn(
  "block w-px transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
);

const navMarkIdle = cn(
  "h-2.5 bg-border-strong opacity-0",
  "group-hover/nav:h-4 group-hover/nav:opacity-100"
);

const navMarkActive = cn("h-[18px] bg-accent opacity-100");

type NavItem = {
  to: string;
  labelKey: "nav.about" | "nav.portfolio" | "nav.contact";
};

const NAV_ITEMS: NavItem[] = [
  { to: "/about", labelKey: "nav.about" },
  { to: "/portfolio", labelKey: "nav.portfolio" },
  { to: "/contact", labelKey: "nav.contact" },
];

const navLinkBase = cn(
  "group/nav relative inline-flex items-center gap-2.5 px-3 py-2",
  "text-[14px] font-medium transition-colors duration-200"
);

export default function Header() {
  const { t } = useTranslation("common");
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lastPath, setLastPath] = useState(location.pathname);

  // Close mobile menu on route change (derived state — no setState-in-effect).
  if (location.pathname !== lastPath) {
    setLastPath(location.pathname);
    if (mobileOpen) setMobileOpen(false);
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-40 transition-[background,backdrop-filter,border] duration-500",
        scrolled
          ? "border-b border-border-subtle bg-bg-base/88 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent backdrop-blur-0"
      )}
    >
      <nav
        className={cn(
          "mx-auto flex max-w-[1240px] items-center justify-between gap-6",
          "px-6 transition-[height] sm:px-8",
          scrolled ? "h-16" : "h-20"
        )}
      >
        <Link
          to="/"
          aria-label={t("brand.fullName")}
          className="group flex items-center gap-3"
        >
          <span
            className={cn(
              "font-display text-[1.6rem] font-medium leading-none",
              "tracking-[-0.04em] text-text-primary",
              "transition-colors group-hover:text-accent"
            )}
          >
            DS<span className="text-accent">.</span>
          </span>
          <span className="block">
            <span className="block label-sm text-text-dim">
              David Stejskal
            </span>
            <span className="block text-[11px] text-text-fade">
              {t("brand.role")}
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 sm:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  navLinkBase,
                  isActive
                    ? "text-text-primary"
                    : "text-text-muted hover:text-text-primary"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    aria-hidden
                    className={cn(
                      navMarkBase,
                      isActive ? navMarkActive : navMarkIdle
                    )}
                  />
                  <span>{t(item.labelKey)}</span>
                </>
              )}
            </NavLink>
          ))}
          <span
            className="mx-3 h-4 w-px bg-border-default"
            aria-hidden
          />
          <LanguageSwitcher />
        </div>

        <button
          type="button"
          className={cn(
            "rounded-md border border-border-subtle p-2 text-text-muted",
            "transition-colors hover:border-border-default",
            "hover:text-text-primary sm:hidden"
          )}
          aria-label={
            mobileOpen
              ? t("accessibility.menu.close")
              : t("accessibility.menu.open")
          }
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </nav>

      {/* Mobile: always visible under nav (fixed header scrolls with viewport). */}
      <div
        className={cn(
          "mx-auto flex max-w-[1240px] justify-end px-6 pb-2.5 pt-1 sm:hidden",
          "border-t transition-[border-color,background] duration-500",
          scrolled
            ? "border-border-subtle bg-bg-base/88 backdrop-blur-xl"
            : "border-border-subtle/60 bg-transparent"
        )}
      >
        <LanguageSwitcher />
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
            "overflow-hidden border-t border-border-subtle",
            "bg-bg-elevated/95 backdrop-blur-xl sm:hidden"
          )}
          >
            <div className="space-y-1 px-6 py-4">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-2 py-3 text-[15px]",
                      "border-b border-border-subtle last:border-b-0",
                      "transition-colors",
                      isActive
                        ? "text-accent"
                        : "text-text-muted hover:text-text-primary"
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        aria-hidden
                        className={cn(
                          "block w-px",
                          isActive
                            ? "h-5 bg-accent"
                            : "h-4 bg-border-strong"
                        )}
                      />
                      <span>{t(item.labelKey)}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
